import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import { useTranslation } from 'react-i18next';
import {
    Invoice,
    InvoiceProtocol,
    InvoiceProtocolPick,
    InvoiceProtocols,
    MergeableSibling,
} from '../models/table-models';
import { transformDateToString } from '../utils/helper-functions';
import { ProtocolPicker } from './ProtocolPicker';
import { useProtocolPicks } from '../utils/useProtocolPicks';
import { useFieldArray, useForm } from 'react-hook-form';
import { InvoiceCorrectionInput, InvoiceBreakdownInput } from '../models/invoice-corrections';
import { InvoiceBreakdownEditor, BreakdownDraft, grossCents } from './InvoiceBreakdownEditor';

/**
 * По колко обекта наведнъж: клиент с над сто обекта не се събира на един екран, а протоколите на всеки
 * ред са по няколко реда високи. Следващите се добавят при скрол до долния край.
 */
const ROWS_PER_CHUNK = 20;

/**
 * Кой обект е това: идентификаторът, защото два обекта на един клиент могат да носят едно име. Името е
 * само последна инстанция - фактура, която не е на обект (продажба), идва без идентификатор.
 */
const branchKey = (row: MergeableSibling) => row.branchId ?? row.branchName;

/**
 * Дозарежда редове, докато операторът скролира - вместо страници, по които отметките да се търсят.
 *
 * Наблюдава се последният ред („котвата") на таблицата, а не позицията на скрола: две таблици делят един
 * и същ скрол в тялото на модала, тоест обща позиция би дозаредила и двете наведнъж. Коренът е самото
 * тяло на модала, за да важи и `rootMargin` - иначе редовете идват чак когато котвата се покаже, а не
 * малко преди това.
 */
const useRowsOnScroll = (total: number) => {
    const [visibleCount, setVisibleCount] = useState(ROWS_PER_CHUNK);
    const anchorRef = useRef<HTMLTableRowElement | null>(null);

    // Смени ли се списъкът - друга фактура, друг клиент - броенето започва отначало.
    useEffect(() => setVisibleCount(ROWS_PER_CHUNK), [total]);

    useEffect(() => {
        const anchor = anchorRef.current;
        if (!anchor || visibleCount >= total) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setVisibleCount((shown) => Math.min(shown + ROWS_PER_CHUNK, total));
                }
            },
            { root: anchor.closest('.modal-body'), rootMargin: '300px' }
        );
        observer.observe(anchor);

        return () => observer.disconnect();
    }, [visibleCount, total]);

    return { visibleCount, anchorRef };
};

/**
 * Диалогът за обединяване на фактури, на едно място.
 *
 * <p>Дотук същият диалог стоеше преписан три пъти - в `AddInvoicesToDocument`, `AddEditInvoice` и
 * `InvoiceView` - с по две еднакви таблици във всеки, тоест шест копия на един и същ ред. Затова
 * промяна, направена на едно от трите, не се виждаше от пътя, по който операторът минава: молив →
 * „Потвърди" върви през `AddEditInvoice`, а не през `AddInvoicesToDocument`.
 *
 * Носи и двете нови неща: реда на текущата фактура (сървърът не я връща сред сестринските, тя е
 * изключената) и протоколите за периода на всеки ред, с отметки.
 */
export const MergeInvoicesDialog = ({
    show,
    onHide,
    currentInvoice,
    siblings,
    onConfirmOnlyThis,
    onMergeSelected,
}: {
    show: boolean;
    onHide: () => void;
    /** Фактурата, от която е започнало номерирането. Влиза в обединението при всички случаи. */
    currentInvoice?: Invoice;
    siblings: MergeableSibling[];
    /** @param picks отметките за текущата фактура - отметките важат и по този път */
    onConfirmOnlyThis: (picks: InvoiceProtocolPick[], corrections?: InvoiceCorrectionInput[], breakdowns?: InvoiceBreakdownInput[]) => Promise<void>;
    /**
     * @param invoicingIds всички фактури за обединяване, с текущата на първо място
     * @param picks отметките по фактури: точно тези протоколи тръгват с мейла
     */
    onMergeSelected: (invoicingIds: number[], picks: InvoiceProtocolPick[], corrections?: InvoiceCorrectionInput[], breakdowns?: InvoiceBreakdownInput[]) => Promise<void>;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'invoice' });

    const [selectedSiblingIds, setSelectedSiblingIds] = useState<Set<number>>(new Set());
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const [error, setError] = useState('');
    const [breakdowns, setBreakdowns] = useState<Record<number, BreakdownDraft[]>>({});

    const { register, control, watch, reset, getValues } = useForm<{
        corrections: { description: string; amount: string; mode: string; targets: string[] }[];
    }>({ defaultValues: { corrections: [] } });
    const { fields, append, remove } = useFieldArray({ control, name: 'corrections' });
    const correctionRows = watch('corrections');

    const allowCorrections = currentInvoice?.documentType === 'invoice'
        && !currentInvoice?.isSaleInvoice && currentInvoice?.currency === 'EUR';

    const currentRow: MergeableSibling | undefined = useMemo(() => {
        if (!currentInvoice?.id) return undefined;
        return {
            id: currentInvoice.id,
            branchId: currentInvoice.branchId,
            branchName: currentInvoice.branchName,
            companyName: currentInvoice.companyName,
            total: currentInvoice.total,
            description: currentInvoice.comment,
            currency: currentInvoice.currency,
            // Сестринските идват от сървъра като ISO низове, а редът на фактурата носи Date - датите се
            // изравняват тук, за да не излезе един ред в друг формат от съседите си.
            fromDate: transformDateToString(currentInvoice.fromDate, 'yyyy-mm-dd'),
            duePaymentDate: transformDateToString(currentInvoice.duePaymentDate, 'yyyy-mm-dd'),
        };
    }, [currentInvoice]);

    const ownBranchRows = useMemo(() => {
        const own = currentRow ? siblings.filter((s) => branchKey(s) === branchKey(currentRow)) : [];
        return currentRow ? [currentRow, ...own] : own;
    }, [siblings, currentRow]);

    const otherBranchRows = useMemo(
        () => (currentRow ? siblings.filter((s) => branchKey(s) !== branchKey(currentRow)) : siblings),
        [siblings, currentRow]
    );

    // При отваряне сестринските са отметнати; протоколите ги тегли useProtocolPicks.
    useEffect(() => {
        if (!show) return;
        setSelectedSiblingIds(new Set(siblings.map((s) => s.id)));
        reset({ corrections: [] });
        setBreakdowns({});
        setError('');
    }, [show, siblings, reset]);

    const rowIds = useMemo(
        () => [...(currentRow ? [currentRow.id] : []), ...siblings.map((s) => s.id)],
        [currentRow, siblings]
    );
    const {
        protocolsByInvoice,
        loading: protocolsLoading,
        selectedProtocolIds,
        toggleProtocol,
        addProtocol,
        addedFor,
        picksFor,
    } = useProtocolPicks(rowIds, show);

    const toggleSibling = (id: number) =>
        setSelectedSiblingIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const toggleGroup = (rows: MergeableSibling[], checked: boolean) =>
        setSelectedSiblingIds((prev) => {
            const next = new Set(prev);
            rows.forEach((row) => {
                if (row.id === currentRow?.id) return;
                if (checked) next.add(row.id);
                else next.delete(row.id);
            });
            return next;
        });

    const submit = async (onlyThis: boolean) => {
        if (submittingRef.current || !currentRow) return;

        submittingRef.current = true;
        setSubmitting(true);
        setError('');

        try {
            const ids = onlyThis ? [currentRow.id] : [currentRow.id, ...Array.from(selectedSiblingIds)];
            const corrections: InvoiceCorrectionInput[] = [];

            if (allowCorrections) {
                for (const row of getValues('corrections')) {
                    const description = row.description.trim();
                    const value = row.amount.trim().replace(',', '.');

                    if (!description && !value) continue;
                    if (!description || !/^[+-]?\d+(\.\d{1,2})?$/.test(value)) {
                        throw new Error(t('corrections.invalidRow'));
                    }

                    const amount = Math.round(Number(value) * 100);

                    if (!Number.isSafeInteger(amount)) throw new Error(t('corrections.invalidAmount'));

                    const targets = row.mode === 'all'
                        ? selectedRows.map(r => r.id) : (row.targets || []).map(Number);

                    if (!targets.length || targets.some(id => !ids.includes(id))) {
                        throw new Error(t('corrections.invalidTargets'));
                    }

                    corrections.push({ description, amount, invoicingIds: targets });
                }
            }

            const breakdownInputs: InvoiceBreakdownInput[] = [];
            if (allowCorrections) for (const id of ids) {
                const draft = breakdowns[id];
                if (!draft) continue;
                const lines = draft.map(line => {
                    const quantity = Number(line.quantity);
                    const grossAmount = grossCents(line.amount);
                    if (!line.description.trim() || !Number.isInteger(quantity) || quantity < 1 || quantity > 1000000 || grossAmount === undefined) {
                        throw new Error(t('breakdown.invalidRow'));
                    }
                    return { description: line.description.trim(), quantity, grossAmount };
                });
                const target = selectedRows.find(row => row.id === id)?.total;
                if (!lines.length || lines.reduce((sum, line) => sum + line.grossAmount, 0) !== target) {
                    throw new Error(t('breakdown.invalidTotal'));
                }
                breakdownInputs.push({ invoicingId: id, lines });
            }
            // Preserve the existing callback contract for documents without a breakdown.
            if (onlyThis) {
                if (breakdownInputs.length) await onConfirmOnlyThis(picksFor(ids), corrections, breakdownInputs);
                else await onConfirmOnlyThis(picksFor(ids), allowCorrections ? corrections : undefined);
            } else {
                if (breakdownInputs.length) await onMergeSelected(ids, picksFor(ids), corrections, breakdownInputs);
                else await onMergeSelected(ids, picksFor(ids), allowCorrections ? corrections : undefined);
            }
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || t('corrections.confirmationFailed'));
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    const selectableSiblings = (rows: MergeableSibling[]) => rows.filter((r) => r.id !== currentRow?.id);

    // Какво влиза в документа: текущата фактура при всички случаи, плюс отметнатите сестрински.
    const selectedRows = useMemo(
        () => [
            ...(currentRow ? [currentRow] : []),
            ...siblings.filter((s) => s.id !== currentRow?.id && selectedSiblingIds.has(s.id)),
        ],
        [currentRow, siblings, selectedSiblingIds]
    );

    const selectedTotal = useMemo(() => selectedRows.reduce((sum, row) => sum + row.total, 0), [selectedRows]);

    const correctionTotal = correctionRows.reduce((sum, row) => {
        const amount = Number(row.amount.replace(',', '.'));
        return sum + (Number.isFinite(amount) ? Math.round(amount * 100) : 0);
    }, 0);

    const currency = currentRow?.currency ?? siblings[0]?.currency ?? '';

    // Един ред е едно задължение, а един обект може да има няколко - затова двете числа се броят отделно.
    const allRows = useMemo(() => [...(currentRow ? [currentRow] : []), ...siblings], [currentRow, siblings]);
    const branchCount = (rows: MergeableSibling[]) => new Set(rows.map(branchKey)).size;

    return (
        // `scrollable`: скролира само тялото, тоест тоталът и бутоните долу стоят на екрана и при сто реда.
        <Modal show={show} onHide={() => !submitting && onHide()} centered size='xl' scrollable backdrop={submitting ? 'static' : true} keyboard={!submitting}>
            <Modal.Header closeButton>
                <Modal.Title>{t('mergeInvoicesTitle')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>{t('mergeInvoicesDescription')}</p>
                <fieldset className='invoice-confirmation-form' disabled={submitting}>
                {ownBranchRows.length > 0 && (
                    <>
                        <div className='d-flex align-items-center mb-2'>
                            <Form.Check
                                type='checkbox'
                                checked={selectableSiblings(ownBranchRows).every((s) => selectedSiblingIds.has(s.id))}
                                disabled={selectableSiblings(ownBranchRows).length === 0}
                                onChange={(e) => toggleGroup(ownBranchRows, e.target.checked)}
                                label={<strong>{t('sameBranch')} ({currentInvoice?.branchName})</strong>}
                            />
                        </div>
                        <MergeRowsTable
                            rows={ownBranchRows}
                            currentInvoiceId={currentRow?.id}
                            selectedSiblingIds={selectedSiblingIds}
                            onToggleSibling={toggleSibling}
                            protocolsByInvoice={protocolsByInvoice}
                            selectedProtocolIds={selectedProtocolIds}
                            onToggleProtocol={toggleProtocol}
                            addedFor={addedFor}
                            onAddProtocol={addProtocol}
                            protocolsLoading={protocolsLoading}
                        />
                    </>
                )}
                {otherBranchRows.length > 0 && (
                    <>
                        <div className='d-flex align-items-center mb-2'>
                            <Form.Check
                                type='checkbox'
                                checked={otherBranchRows.every((s) => selectedSiblingIds.has(s.id))}
                                onChange={(e) => toggleGroup(otherBranchRows, e.target.checked)}
                                label={<strong>{t('otherBranches')}</strong>}
                            />
                        </div>
                        <MergeRowsTable
                            rows={otherBranchRows}
                            currentInvoiceId={currentRow?.id}
                            selectedSiblingIds={selectedSiblingIds}
                            onToggleSibling={toggleSibling}
                            protocolsByInvoice={protocolsByInvoice}
                            selectedProtocolIds={selectedProtocolIds}
                            onToggleProtocol={toggleProtocol}
                            addedFor={addedFor}
                            onAddProtocol={addProtocol}
                            protocolsLoading={protocolsLoading}
                        />
                    </>
                )}
                {allowCorrections && <section className='mt-3'>
                    <h5>{t('breakdown.title')}</h5>
                    <p className='small text-muted'>{t('breakdown.hint')}</p>
                    {selectedRows.map(row => <div className='border rounded p-3 mb-2' key={row.id}>
                        <strong>{row.branchName}: {row.fromDate} – {row.duePaymentDate}</strong>
                        <Button className='ms-2' variant='outline-secondary' onClick={() => setBreakdowns(previous => {
                            const next = { ...previous };
                            if (next[row.id]) delete next[row.id];
                            else next[row.id] = [{ description: row.description || t('breakdown.defaultDescription', { branch: row.branchName, from: row.fromDate, to: row.duePaymentDate }),
                                quantity: '1', amount: (row.total / 100).toFixed(2) }];
                            return next;
                        })}>{t(breakdowns[row.id] ? 'breakdown.restore' : 'breakdown.edit')}</Button>
                        {breakdowns[row.id] && <InvoiceBreakdownEditor initial={breakdowns[row.id]} total={row.total}
                            onChange={lines => setBreakdowns(previous => ({ ...previous, [row.id]: lines }))} />}
                    </div>)}
                </section>}
                {allowCorrections && <section className='mt-3'>
                    <h5>{t('corrections.title')}</h5>
                    <p className='small text-muted'>{t('corrections.hint')}</p>
                    {fields.map((field, index) => <div key={field.id} className='border rounded p-3 mb-2'>
                        <div className='d-flex flex-column flex-lg-row align-items-lg-start gap-3 mb-2'>
                            <div className='flex-grow-1' style={{ minWidth: 0 }}>
                                <Form.Label htmlFor={`correction-${field.id}-description`}>{t('corrections.description')}</Form.Label>
                                <TextareaAutosize
                                    id={`correction-${field.id}-description`}
                                    className='form-control invoice-correction-description'
                                    minRows={1}
                                    style={{ width: '100%', resize: 'none' }}
                                    aria-label={t('corrections.descriptionLabel', { index: index + 1 })}
                                    maxLength={1000}
                                    {...register(`corrections.${index}.description` as const)} />
                            </div>
                            <div className='flex-shrink-0' style={{ width: '11rem', maxWidth: '100%' }}>
                                <Form.Label htmlFor={`correction-${field.id}-amount`}>{t('corrections.amount')}</Form.Label>
                                <Form.Control id={`correction-${field.id}-amount`} aria-label={t('corrections.amountLabel', { index: index + 1 })} inputMode='decimal' {...register(`corrections.${index}.amount` as const)} />
                            </div>
                        </div>
                        <div className='d-flex flex-column flex-lg-row align-items-lg-start gap-3'>
                            <div className='flex-grow-1' style={{ minWidth: 0 }}>
                                <Form.Label htmlFor={`correction-${field.id}-mode`}>{t('corrections.distribution')}</Form.Label>
                                <Form.Select id={`correction-${field.id}-mode`} className='mb-2' {...register(`corrections.${index}.mode` as const)}>
                                    <option value='all'>{t('corrections.allObligations')}</option>
                                    <option value='selected'>{t('corrections.selectedObligations')}</option>
                                </Form.Select>
                        {correctionRows[index]?.mode === 'selected' && selectedRows.map(row =>
                            <Form.Check key={row.id} className='d-flex align-items-center gap-2 ps-0'>
                                <Form.Check.Input
                                    id={`correction-${field.id}-target-${row.id}`}
                                    type='checkbox'
                                    className='float-none m-0 flex-shrink-0'
                                    value={String(row.id)}
                                    {...register(`corrections.${index}.targets` as const)} />
                                <Form.Check.Label
                                    htmlFor={`correction-${field.id}-target-${row.id}`}
                                    className='mb-0'>
                                    {`${row.branchName}: ${row.fromDate} – ${row.duePaymentDate} (${(row.total / 100).toFixed(2)} EUR)`}
                                </Form.Check.Label>
                             </Form.Check>)}
                            </div>
                            <div className='flex-shrink-0'>
                                <div className='d-none d-lg-block mb-2' aria-hidden='true'>&nbsp;</div>
                                <Button variant='outline-danger' onClick={() => remove(index)}>{t('corrections.removeRow')}</Button>
                            </div>
                        </div>
                    </div>)}
                    <Button variant='outline-primary' disabled={fields.length >= 100}
                        onClick={() => append({ description: '', amount: '', mode: 'all', targets: [] })}>{t('corrections.addRow')}</Button>
                </section>}
                </fieldset>
                {error && <div role='alert' className='alert alert-danger mt-3'>{error}</div>}
            </Modal.Body>
            <Modal.Footer>
                <div className='me-auto'>
                    <div>
                        {t('selectedTotal')}: <strong>{(selectedTotal / 100).toFixed(2)} {currency}</strong>
                        {allowCorrections && <div>{t('corrections.adjustedTotal')}: <strong>{((selectedTotal + correctionTotal) / 100).toFixed(2)} EUR</strong></div>}
                    </div>
                    {/* Броевете са тук, защото отметнатото по-надолу в списъка не се вижда наведнъж. */}
                    <div className='small text-muted'>
                        {t('selectedObligations', { selected: selectedRows.length, available: allRows.length })}
                        {' · '}
                        {t('selectedBranches', {
                            selected: branchCount(selectedRows),
                            available: branchCount(allRows),
                        })}
                    </div>
                </div>
                <Button variant='secondary' onClick={() => submit(true)} disabled={submitting || protocolsLoading || !currentRow}>
                    {submitting && <Spinner animation='border' size='sm' className='me-2' />}
                    {t('confirmOnlyThis')}
                </Button>
                <Button variant='success' onClick={() => submit(false)} disabled={submitting || protocolsLoading || selectedSiblingIds.size === 0}>
                    {t('mergeSelected')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/**
 * Редовете: фактура, период и протоколите за периода ѝ с отметки.
 *
 * Редът на текущата фактура е отметнат и заключен - той влиза в обединението при всички случаи, а
 * операторът трябва да го вижда, за да отметне и неговите протоколи. Затова той стои закотвен на първо
 * място и не участва в дозареждането: иначе се изгубва надолу заедно с останалите.
 *
 * Останалите идват по {@link ROWS_PER_CHUNK} при скрол. Отметките - и на редове, и на протоколи - се
 * държат по идентификатор извън таблицата, тоест „Обедини избраните" праща цялата група, а не само
 * дозаредените редове.
 *
 * Показват се протоколите от периода на задължението. Тези от началото на месеца преди периода стоят под
 * „+": често отговарят на същата фактура, защото обслужването пада на границата на отчетния ден, но не
 * бива да излизат сами.
 */
const MergeRowsTable = ({
    rows,
    currentInvoiceId,
    selectedSiblingIds,
    onToggleSibling,
    protocolsByInvoice,
    selectedProtocolIds,
    onToggleProtocol,
    addedFor,
    onAddProtocol,
    protocolsLoading,
}: {
    rows: MergeableSibling[];
    currentInvoiceId?: number;
    selectedSiblingIds: Set<number>;
    onToggleSibling: (id: number) => void;
    protocolsByInvoice: Map<number, InvoiceProtocols>;
    selectedProtocolIds: Set<number>;
    onToggleProtocol: (id: number) => void;
    addedFor: (invoicingId: number) => InvoiceProtocol[];
    onAddProtocol: (invoicingId: number, protocol: InvoiceProtocol) => void;
    protocolsLoading: boolean;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'invoice' });

    const pinnedRow = useMemo(() => rows.find((row) => row.id === currentInvoiceId), [rows, currentInvoiceId]);
    const scrollableRows = useMemo(() => rows.filter((row) => row.id !== currentInvoiceId), [rows, currentInvoiceId]);

    const { visibleCount, anchorRef } = useRowsOnScroll(scrollableRows.length);

    const shownRows = useMemo(
        () => [...(pinnedRow ? [pinnedRow] : []), ...scrollableRows.slice(0, visibleCount)],
        [pinnedRow, scrollableRows, visibleCount]
    );

    const hasMore = visibleCount < scrollableRows.length;

    return (
        <table className='table table-sm table-bordered mb-3'>
            <thead>
                <tr>
                    <th></th>
                    <th>{t('branch')}</th>
                    <th>{t('total')}</th>
                    <th>{t('period')}</th>
                    <th>{t('protocolsForPeriod')}</th>
                </tr>
            </thead>
            <tbody>
                {shownRows.map((row) => {
                    const isCurrent = row.id === currentInvoiceId;
                    const withProtocols = protocolsByInvoice.get(row.id);
                    return (
                        <tr key={row.id}>
                            <td>
                                <Form.Check
                                    type='checkbox'
                                    checked={isCurrent || selectedSiblingIds.has(row.id)}
                                    disabled={isCurrent}
                                    onChange={() => onToggleSibling(row.id)}
                                />
                            </td>
                            <td>
                                {row.branchName}
                                {isCurrent && <div className='small text-muted'>{t('currentInvoice')}</div>}
                            </td>
                            <td>
                                {(row.total / 100).toFixed(2)} {row.currency}
                            </td>
                            <td>
                                {row.fromDate} - {row.duePaymentDate}
                            </td>
                            <td>
                                <ProtocolPicker
                                    row={withProtocols}
                                    loading={protocolsLoading}
                                    selectedProtocolIds={selectedProtocolIds}
                                    onToggleProtocol={onToggleProtocol}
                                    added={addedFor(row.id)}
                                    onAddProtocol={(protocol) => onAddProtocol(row.id, protocol)}
                                />
                            </td>
                        </tr>
                    );
                })}
                {/* Котвата: покаже ли се, следващите редове влизат в таблицата. */}
                {hasMore && (
                    <tr ref={anchorRef}>
                        <td colSpan={5} className='text-center small text-muted'>
                            <Spinner animation='border' size='sm' className='me-2' />
                            {t('mergedShownOf', { shown: shownRows.length, count: rows.length })}
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
};
